import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert
} from '@mui/material';
import { PaymentProcessRequest, PaymentMethod, StaffPayrollDto } from '../../services/payrollService';
import { payrollService } from '../../services/payrollService';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onProcessPayment: () => void;
  loading: boolean;
  paymentForm: PaymentProcessRequest;
  onFormChange: (form: PaymentProcessRequest) => void;
  payroll?: StaffPayrollDto | null;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  onProcessPayment,
  loading,
  paymentForm,
  onFormChange,
  payroll
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process Payment</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Stack spacing={3}>
            {/* Payroll summary */}
            {payroll && (
              <Alert severity="info" icon={false} sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" fontWeight="bold">{payroll.staffName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {payroll.employeeId} &nbsp;·&nbsp; {payrollService.formatDate(payroll.payrollStartDate)} – {payrollService.formatDate(payroll.payrollEndDate)}
                  </Typography>
                  <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Gross Pay</Typography>
                      <Typography variant="body2" fontWeight="bold">{payrollService.formatCurrency(payroll.grossPay)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Deductions</Typography>
                      <Typography variant="body2" color="error.main">−{payrollService.formatCurrency(payroll.taxDeductions + payroll.otherDeductions)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Net Pay</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">{payrollService.formatCurrency(payroll.netPay)}</Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Alert>
            )}

            <Divider />

            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentForm.paymentMethod}
                label="Payment Method"
                onChange={(e) => onFormChange({ 
                  ...paymentForm, 
                  paymentMethod: e.target.value as PaymentMethod 
                })}
              >
                <MenuItem value={PaymentMethod.DirectDeposit}>Direct Deposit</MenuItem>
                <MenuItem value={PaymentMethod.BankTransfer}>Bank Transfer</MenuItem>
                <MenuItem value={PaymentMethod.Check}>Check</MenuItem>
                <MenuItem value={PaymentMethod.Cash}>Cash</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Transaction ID"
              value={paymentForm.transactionId}
              onChange={(e) => onFormChange({ ...paymentForm, transactionId: e.target.value })}
              required
              helperText="Required — enter the bank transaction or reference number"
            />
            <TextField
              fullWidth
              label="Payment Reference (optional)"
              value={paymentForm.paymentReference}
              onChange={(e) => onFormChange({ ...paymentForm, paymentReference: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Payment Notes (optional)"
              value={paymentForm.paymentNotes}
              onChange={(e) => onFormChange({ ...paymentForm, paymentNotes: e.target.value })}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={onProcessPayment} 
          variant="contained"
          color="success"
          disabled={loading || !paymentForm.transactionId?.trim()}
        >
          {loading ? 'Processing…' : `Mark as Paid — ${payroll ? payrollService.formatCurrency(payroll.netPay) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
